import type {
  BacktestConfig,
  BacktestResult,
  BacktestMetrics,
  Candle,
  SignalType,
  StrategyFn,
  Trade,
} from "../types";

const CONTRACT_SIZE = 100; // 1 standard lot = 100 oz XAUUSD

interface OpenPosition {
  action: "BUY" | "SELL";
  entryTime: number;
  entryPrice: number;
  sl: number;
  tp: number;
  lots: number;
  barsHeld: number;
}

const DEFAULT_CONFIG: BacktestConfig = {
  initialBalance: 10000,
  lots: 0.1,
  slDistance: 4.0, // $4.00 adverse move (40 pips gold)
  tpDistance: 8.0, // $8.00 favorable move (80 pips gold)
  spread: 0.25, // typical XAUUSD spread
  slippage: 0.05,
  commissionPerLot: 0, // most prop-firm accounts: commission folded into spread
  warmupBars: 50,
};

export class ClientSideBacktester {
  private readonly cfg: BacktestConfig;
  private tradeId = 0;

  constructor(config: Partial<BacktestConfig> = {}) {
    this.cfg = { ...DEFAULT_CONFIG, ...config };
  }

  public runBacktest(candles: Candle[], strategyFn: StrategyFn): BacktestResult {
    const { cfg } = this;
    const trades: Trade[] = [];
    const equityCurve: Array<{ time: number; equity: number }> = [];

    let balance = cfg.initialBalance;
    let open: OpenPosition | null = null;

    for (let index = 0; index < candles.length; index++) {
      const candle = candles[index];

      // ── 1. Manage exits on the open position (conservative: SL checked before TP)
      if (open) {
        open.barsHeld += 1;
        const exit = this.checkExit(open, candle);
        if (exit) {
          const trade = this.closePosition(open, exit, candle);
          balance += trade.pnl;
          trades.push(trade);
          open = null;
        }
      }

      // ── 2. Strategy evaluation (after warm-up)
      if (index >= cfg.warmupBars) {
        const history = candles.slice(0, index + 1);
        const signal: SignalType = strategyFn(candle, index, history);

        if (signal && signal !== open?.action) {
          // Close on flip (if any), then open the new direction
          if (open) {
            const trade = this.closePosition(
              open,
              { price: candle.close, reason: "SIGNAL_FLIP" },
              candle
            );
            balance += trade.pnl;
            trades.push(trade);
            open = null;
          }
          open = this.openPosition(signal, candle);
        }
      }

      // ── 3. Mark-to-market equity snapshot
      const floating = open ? this.floatingPnl(open, candle.close) : 0;
      equityCurve.push({ time: candle.time, equity: balance + floating });
    }

    // ── 4. Force-close any position at end of data
    if (open) {
      const last = candles[candles.length - 1];
      const trade = this.closePosition(open, { price: last.close, reason: "EOD" }, last);
      balance += trade.pnl;
      trades.push(trade);
      open = null;
    }

    const metrics = this.calculateMetrics(cfg.initialBalance, balance, trades, equityCurve, candles);
    return { metrics, trades, equityCurve };
  }

  // ───────────────────────── Order lifecycle ─────────────────────────

  private openPosition(action: "BUY" | "SELL", candle: Candle): OpenPosition {
    const { cfg } = this;
    // Bid/Ask model: BUY fills at ask (close + spread), SELL fills at bid (close)
    const entryPrice = action === "BUY" ? candle.close + cfg.spread : candle.close;
    const sl =
      action === "BUY" ? entryPrice - cfg.slDistance : entryPrice + cfg.slDistance;
    const tp =
      action === "BUY" ? entryPrice + cfg.tpDistance : entryPrice - cfg.tpDistance;

    return {
      action,
      entryTime: candle.time,
      entryPrice,
      sl,
      tp,
      lots: cfg.lots,
      barsHeld: 0,
    };
  }

  private checkExit(
    open: OpenPosition,
    candle: Candle
  ): { price: number; reason: "TP" | "SL" } | null {
    const { cfg } = this;

    if (open.action === "BUY") {
      // Conservative fill assumption: SL evaluated before TP within the same bar
      if (candle.low <= open.sl) {
        return { price: open.sl - cfg.slippage, reason: "SL" };
      }
      if (candle.high >= open.tp) {
        return { price: open.tp - cfg.slippage, reason: "TP" };
      }
    } else {
      if (candle.high >= open.sl) {
        return { price: open.sl + cfg.slippage, reason: "SL" };
      }
      if (candle.low <= open.tp) {
        return { price: open.tp + cfg.slippage, reason: "TP" };
      }
    }
    return null;
  }

  private closePosition(
    open: OpenPosition,
    exit: { price: number; reason: "TP" | "SL" | "EOD" | "SIGNAL_FLIP" },
    candle: Candle
  ): Trade {
    const { cfg } = this;
    const dir = open.action === "BUY" ? 1 : -1;
    const gross = (exit.price - open.entryPrice) * dir * open.lots * CONTRACT_SIZE;
    const commission = cfg.commissionPerLot * open.lots;
    const pnl = gross - commission;

    return {
      id: ++this.tradeId,
      action: open.action,
      entryTime: open.entryTime,
      entryPrice: open.entryPrice,
      exitTime: candle.time,
      exitPrice: exit.price,
      sl: open.sl,
      tp: open.tp,
      lots: open.lots,
      pnl,
      barsHeld: open.barsHeld,
      exitReason: exit.reason,
      outcome: pnl > 0 ? "WIN" : pnl < 0 ? "LOSS" : "BREAKEVEN",
    };
  }

  private floatingPnl(open: OpenPosition, markPrice: number): number {
    const dir = open.action === "BUY" ? 1 : -1;
    return (markPrice - open.entryPrice) * dir * open.lots * CONTRACT_SIZE;
  }

  // ───────────────────────── Metrics ─────────────────────────

  private calculateMetrics(
    initialBalance: number,
    finalBalance: number,
    trades: Trade[],
    equityCurve: Array<{ time: number; equity: number }>,
    candles: Candle[]
  ): BacktestMetrics {
    const wins = trades.filter((t) => t.outcome === "WIN");
    const losses = trades.filter((t) => t.outcome === "LOSS");
    const breakeven = trades.filter((t) => t.outcome === "BREAKEVEN");

    const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;

    // Max drawdown from equity peak (realized + floating)
    let peak = -Infinity;
    let maxDrawdown = 0;
    let maxDrawdownPct = 0;
    for (const point of equityCurve) {
      if (point.equity > peak) peak = point.equity;
      const dd = peak - point.equity;
      if (dd > maxDrawdown) {
        maxDrawdown = dd;
        maxDrawdownPct = peak > 0 ? (dd / peak) * 100 : 0;
      }
    }

    // Max consecutive losses
    let maxConsecLosses = 0;
    let currentStreak = 0;
    for (const t of trades) {
      if (t.outcome === "LOSS") {
        currentStreak += 1;
        if (currentStreak > maxConsecLosses) maxConsecLosses = currentStreak;
      } else {
        currentStreak = 0;
      }
    }

    // Sharpe ratio from per-bar equity returns, annualized by bar frequency
    const sharpeRatio = this.computeSharpe(equityCurve, candles);

    const totalTrades = trades.length;
    const avgWin = wins.length > 0 ? grossProfit / wins.length : 0;
    const avgLoss = losses.length > 0 ? grossLoss / losses.length : 0;

    return {
      initialBalance,
      finalBalance,
      netProfit: finalBalance - initialBalance,
      netProfitPct: initialBalance > 0 ? ((finalBalance - initialBalance) / initialBalance) * 100 : 0,
      totalTrades,
      wins: wins.length,
      losses: losses.length,
      breakeven: breakeven.length,
      winRate: totalTrades > 0 ? (wins.length / totalTrades) * 100 : 0,
      profitFactor,
      maxDrawdown,
      maxDrawdownPct,
      sharpeRatio,
      avgWin,
      avgLoss,
      expectancy: totalTrades > 0 ? (finalBalance - initialBalance) / totalTrades : 0,
      maxConsecutiveLosses: maxConsecLosses,
      avgBarsHeld: totalTrades > 0 ? trades.reduce((s, t) => s + t.barsHeld, 0) / totalTrades : 0,
    };
  }

  private computeSharpe(
    equityCurve: Array<{ time: number; equity: number }>,
    candles: Candle[]
  ): number {
    if (equityCurve.length < 3) return 0;

    const returns: number[] = [];
    for (let i = 1; i < equityCurve.length; i++) {
      const prev = equityCurve[i - 1].equity;
      if (prev > 0) returns.push((equityCurve[i].equity - prev) / prev);
    }
    if (returns.length < 2) return 0;

    const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
    const variance =
      returns.reduce((s, r) => s + (r - mean) ** 2, 0) / (returns.length - 1);
    const std = Math.sqrt(variance);
    if (std === 0) return 0;

    // Annualization factor from candle spacing (default M1 → 525600 bars/year)
    let barSeconds = 60;
    if (candles.length >= 2) barSeconds = Math.max(1, candles[1].time - candles[0].time);
    const barsPerYear = 31_536_000 / barSeconds;

    return (mean / std) * Math.sqrt(barsPerYear);
  }
}
